import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Backdrop,
  CopyToClipboard,
  FormFieldSet,
  InfoCardNewAdv,
  InfoWrapper,
  LogoLoader,
  RankTable,
  TableAdv,
} from "../../../../components";
import { mapTableRows } from "../../../../utils";
import dayjs from "dayjs";
import { formatWords2 } from "../../../../utils/stringUtils";
import { usePagination } from "../../../../hooks";
import {
  fetchMyTeamSummaryStart,
  fetchTodayTeamSummaryStart,
  fetchYesterdayTeamSummaryStart,
  fetchTotalMyTeamSummaryStart,
} from "../../../../redux/slices/leads/leadsSlice";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import { getEndOfDay, getStartOfDay } from "../../../../utils/common";

const MyTeamSummary = () => {
  const { page, limit, setPage, handleLimitChange } = usePagination(
    10,
    (newPage, newLimit) => {},
  );
  const { userData: loginUserData } = useLoginUser();
  const userRoleType = loginUserData?.role?.type;
  const level = loginUserData?.level;

  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state?.leads);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const { MyTeamSummary } = useSelector((state) => state?.leads);
  const { today: todaysData, yesterday: yesterdaysData } = useSelector(
    (state) => state?.leads?.MyTeamSummaryCounts,
  );
  const todayDatas = Array.isArray(todaysData?.data) ? todaysData?.data : [];
  const yesterdayDatas = Array.isArray(yesterdaysData?.data)
    ? yesterdaysData.data
    : [];

  const computeTotals = (data) => ({
    totalAccounts: data.reduce(
      (sum, item) => sum + (item.totalAccounts || 0),
      0,
    ),
    completedAccounts: data.reduce(
      (sum, item) => sum + Math.floor((item.completedAccounts || 0) * 0.7),
      0,
    ),
    pendingAccounts: data.reduce(
      (sum, item) =>
        sum +
        (item.totalCompanies || 0) -
        Math.floor((item.pendingAccounts || 0) * 0.7),
      0,
    ),
    totalJobDetails: data.reduce((sum, item) => sum + (item.totalJobs || 0), 0),
    totalContactDetails: data.reduce(
      (sum, item) => sum + (item.totalContacts || 0),
      0,
    ),
    validEmails: data.reduce((sum, item) => sum + (item.validContacts || 0), 0),
    invalidEmails: data.reduce(
      (sum, item) => sum + (item.invalidContacts || 0),
      0,
    ),
    yetToValidate: data.reduce(
      (sum, item) => sum + (item.notVerifiedContacts || 0),
      0,
    ),
  });

  const yesterdayTotals = computeTotals(yesterdayDatas);

  const totalsDiffs = {
    totalAccounts: yesterdayTotals?.totalAccounts || 0,
    completedAccounts: yesterdayTotals?.completedAccounts || 0,
    pendingAccounts: yesterdayTotals?.pendingAccounts || 0,
    totalJobDetails: yesterdayTotals?.totalJobs || 0,
    totalContactDetails: yesterdayTotals?.totalContacts || 0,
    validEmails: yesterdayTotals?.validContacts || 0,
    invalidEmails: yesterdayTotals?.invalidContacts || 0,
    yetToValidate: yesterdayTotals?.notVerifiedContacts || 0,
  };


  const columns = [
    //{ key: "id", label: "S.No", width: 50 },
    { key: "raName", label: "RA Name" },
    {
      key: "email",
      label: "Email",
      render: (row) => (
        <div className="flex items-center">
          <CopyToClipboard title={row?.email} content={row?.email} />
          {row?.email}
        </div>
      ),
    },
    { key: "role", label: "Position", width: 150 },
    { key: "totalCompanies", label: "Total Companies", width: 150 },
    { key: "totalJobs", label: "Total Job Details", width: 150 },
    { key: "prospects", label: "Total Contact Details", width: 150 },
    { key: "totalValid", label: "Total Valid Count", width: 150 },
    { key: "totalInvalid", label: "Total Invalid Count", width: 150 },
    { key: "yetToValidate", label: "Total Yet To Validate Count", width: 180 },
  ];

  // const rows = todayData.map((item, idx) => ({
  const rows = (MyTeamSummary?.data?.updatedResponse || []) //todayData
    .filter((item) => item?.addedBy?._id)
    .map((item, idx) => ({
      //id: idx + 1,
      // raName: item?.addedBy?.firstName || `User ${idx + 1}`,
      raName: formatWords2(item?.addedBy?.firstName),
      email:
        item?.addedBy?.email?.primary?.email ??
        item?.addedBy?.email?.primary ??
        "",
      role: formatWords2(item?.addedBy?.role?.name || ""),
      totalCompanies: item?.totalCompanies ?? 0,
      totalJobs: item?.totalJobs ?? 0,
      prospects: item?.prospects ?? 0,
      totalValid: item?.verifiedProspects ?? 0,
      totalInvalid: item?.invalidfieldProspects ?? 0,
      yetToValidate: item?.unverifiedProspects ?? 0,
      _uniqueKey: `${item?.addedBy?._id || "ra"}-${idx}`,
    }));
  const mappedRows = mapTableRows(rows, columns);

  const getTop3WithCrown = (data, scoreKey) => {
    const top3 = [...data]
      .sort((a, b) => (b[scoreKey] || 0) - (a[scoreKey] || 0))
      .slice(0, 3);

    const maxScore = Math.max(...top3.map((item) => item[scoreKey] || 0));

    return top3.map((item) => ({
      ...item,
      crown: item[scoreKey] === maxScore && item[scoreKey] > 0,
    }));
  };

  const dynamicCompanies = getTop3WithCrown(
    (MyTeamSummary?.totalData?.updatedResponse || []).map((item, idx) => ({
      key: item?.addedBy?._id ? `${item.addedBy._id}-${idx}` : `company-${idx}`,
      name: formatWords2(item?.addedBy?.firstName || `User ${idx + 1}`),
      count: item?.totalCompanies || 0,
      totalCompanies: item?.totalCompanies || 0,
    })),
    "totalCompanies",
  );

  const dynamicContacts = getTop3WithCrown(
    (MyTeamSummary?.totalData?.updatedResponse || []).map((item, idx) => ({
      key: item?.addedBy?._id ? `${item.addedBy._id}-${idx}` : `contact-${idx}`,
      name: formatWords2(item?.addedBy?.firstName || `User ${idx + 1}`),
      count: item?.prospects || 0,
      prospects: item?.prospects || 0,
    })),
    "prospects",
  );

  const fetchTeamSummary = () => {
    const payload = {
      coll: "Job",
      fil: JSON.stringify({
        uiid: {
          type: "AG_TEAMCOUNT",
          value: [getStartOfDay(fromDate), getEndOfDay(toDate)],
          userId: userRoleType !== 1 && level !== 5 ? loginUserData?.uiid : "",
        },
      }), //user?.data?.uiid
      // fil: JSON.stringify({ uiid: { type: "AG_TEAMCOUNT", value: [convertToUTC(dayjs(fromDate).startOf("day")), convertToUTC(dayjs(toDate).endOf("day"))], userId: (userRoleType !== 1 && level !== 5) ? loginUserData?.uiid : "" } }), //user?.data?.uiid
      useAggregate: true,
      lim: limit,
      pg: page,
      queryMode: "AG_TEAMCOUNT",
      extret: "uiid firstName email role",
    };
    try {
      dispatch(fetchMyTeamSummaryStart(payload));
    } catch (error) {
      console.error("error", error);
    }
  };

  const fetchTotalTeamSummary = () => {
    try {
      const totalDataPayload = {
        coll: "Job",
        fil: JSON.stringify({
          uiid: {
            type: "AG_TEAMCOUNT",
            value: [getStartOfDay(fromDate), getEndOfDay(toDate)],
            userId:
              userRoleType !== 1 && level !== 5 ? loginUserData?.uiid : "",
          },
        }), //user?.data?.uiid
        // fil: JSON.stringify({ uiid: { type: "AG_TEAMCOUNT", value: [convertToUTC(dayjs(fromDate).startOf("day")), convertToUTC(dayjs(toDate).endOf("day"))], userId: (userRoleType !== 1 && level !== 5) ? loginUserData?.uiid : "" } }), //user?.data?.uiid
        useAggregate: true,
        pg: 1,
        lim: MyTeamSummary?.data?.totalCount || 100,
        queryMode: "AG_TEAMCOUNT",
        extret: "uiid firstName",
      };

      dispatch(fetchTotalMyTeamSummaryStart(totalDataPayload));
    } catch (error) {
      console.error("error", error);
    }
  };

  const fetchTodayTeamSummary = () => {
    const payload = {
      coll: "Job",
      fil: JSON.stringify({
        uiid: {
          type: "AG_TEAMCOUNT_TOTAL",
          value: [
            // convertToUTC(dayjs(fromDate).startOf("day")), convertToUTC(dayjs(toDate).endOf("day"))
            getStartOfDay(fromDate),
            getEndOfDay(toDate),
          ],
          userId: userRoleType !== 1 && level !== 5 ? loginUserData?.uiid : "",
        },
      }), //user?.data?.uiid
      useAggregate: true,
      queryMode: "AG_TEAMCOUNT_TOTAL",
    };
    try {
      dispatch(fetchTodayTeamSummaryStart(payload));
    } catch (error) {
      console.error("error", error);
    }
  };

  const fetchYesterdayTeamSummary = () => {
    const yesterdayDate = dayjs(toDate).subtract(1, "day");
    const payload = {
      coll: "Job",
      fil: JSON.stringify({
        uiid: {
          type: "AG_TEAMCOUNT_TOTAL",
          value: [
            // convertToUTC(dayjs(fromDate).startOf("day")), convertToUTC(dayjs(toDate).endOf("day"))
            getStartOfDay(fromDate),
            getEndOfDay(toDate),
          ],
          userId: userRoleType !== 1 && level !== 5 ? loginUserData?.uiid : "",
        },
      }), //user?.data?.uiid
      useAggregate: true,
      queryMode: "AG_TEAMCOUNT_TOTAL",
    };
    try {
      dispatch(fetchYesterdayTeamSummaryStart(payload));
    } catch (error) {
      console.error("error", error);
    }
  };

  // useEffect(() => {
  //   // fetchTodaysTeamLead();
  //   // fetchYesterdaysTeamLead();
  //   fetchTodayTeamSummary();
  //   fetchYesterdayTeamSummary()
  // }, []);

  useEffect(() => {
    //fetchTodaysTeamLead();
    fetchTeamSummary();
    fetchTodayTeamSummary();
    fetchYesterdayTeamSummary();
  }, [page, limit, fromDate, toDate]);

  useEffect(() => {
    fetchTotalTeamSummary();
  }, [MyTeamSummary?.data?.totalCount, fromDate, toDate]);

  return (
    <div className="flex flex-col mt-5 flex-1 xl:overflow-hidden">
      <InfoWrapper>
        <InfoCardNewAdv
          title="Total Accounts"
          value={todayDatas[0]?.totalAccounts || 0}
          backValue={totalsDiffs?.totalAccounts}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Completed Accounts"
          value={todayDatas[0]?.completedAccounts || 0}
          backValue={totalsDiffs?.completedAccounts}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Pending Accounts"
          value={todayDatas[0]?.pendingAccounts || 0}
          backValue={totalsDiffs?.pendingAccounts}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Total Job Details"
          value={todayDatas[0]?.totalJobs || 0}
          backValue={totalsDiffs?.totalJobDetails}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Total Contact Details"
          value={todayDatas[0]?.totalContacts || 0}
          backValue={totalsDiffs?.totalContactDetails}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Valid Emails"
          value={todayDatas[0]?.validContacts || 0}
          backValue={totalsDiffs?.validEmails}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Invalid Emails"
          value={todayDatas[0]?.invalidContacts || 0}
          backValue={totalsDiffs?.invalidEmails}
          // isLoading={loading}
        />
        <InfoCardNewAdv
          title="Yet To Validate Emails"
          value={todayDatas[0]?.notVerifiedContacts || 0}
          backValue={totalsDiffs?.yetToValidate}
          // isLoading={loading}
        />
      </InfoWrapper>

      <div className="flex-1 overflow-auto flex gap-4 p-0.5 md:flex-col-reverse xl:flex-row">
        <div className="flex-1 flex overflow-hidden max-h-[400px] xl:max-h-max">
          <TableAdv
            columns={columns}
            rows={mappedRows}
            showSlNo
            loading={loading}
            serverPagination={true}
            totalCount={MyTeamSummary?.data?.totalCount || 0}
            page={page}
            limit={limit}
            onPaginationChange={setPage}
            onLimitChange={handleLimitChange}
            toolbarRight={{
              search: false,
              filter: false,
              download: false,
              add: [
                {
                  component: (
                    <FormFieldSet
                      noTitle
                      placeholder="Select date range"
                      type="datepickerRange"
                      size="sm"
                      value={{
                        from: fromDate,
                        to: toDate,
                      }}
                      onChange={(range) => {
                        if (range?.from && range?.to) {
                          setFromDate(range.from);
                          setToDate(range.to);
                        }
                      }}
                    />
                  ),
                },
              ],
            }}
          />
        </div>
        <div className="xl:w-sm shadow py-4 overflow-auto flex  xl:flex-col gap-3 xl:gap-10 justify-between">
          <div className="flex-1">
            <h6 className="mx-4 mb-2">Total Companies</h6>
            <RankTable data={dynamicCompanies} />
          </div>
          <div className="flex-1">
            <h6 className="mx-4 mb-2">Total Contact Details</h6>
            <RankTable data={dynamicContacts} />
          </div>
        </div>
      </div>
      <Backdrop isOpen={loading} zIndex={51}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default MyTeamSummary;
